import { ApplicationTypeConfig } from '@context/shared/types/chemical';
/**
 * Organization config subcollection at /organizations/{orgId}/config.
 * Each document represents configuration for a specific area of the app.
 */

/** Collection id for organization config documents. */
export const ORGANIZATION_CONFIG_COLLECTION_ID = 'config' as const;

/**
 * Document id for chemicals-area config at /organizations/{orgId}/config/chemicals.
 */
export const ORGANIZATION_CONFIG_CHEMICALS_DOC_ID = 'chemicals' as const;

/**
 * Document id for authentication-area config at /organizations/{orgId}/config/authentication.
 */
export const ORGANIZATION_CONFIG_AUTHENTICATION_DOC_ID =
	'authentication' as const;

/**
 * Document id for users-area config at /organizations/{orgId}/config/users.
 */
export const ORGANIZATION_CONFIG_USERS_DOC_ID = 'users' as const;

/** Default invite validity when the users config document is absent. */
export const DEFAULT_INVITE_EXPIRATION_DAYS = 7;

/** Minimum invite validity in days (inclusive). */
export const MIN_INVITE_EXPIRATION_DAYS = 1;

/** Maximum invite validity in days (inclusive, one year). */
export const MAX_INVITE_EXPIRATION_DAYS = 365;

/** Default guest auth session TTL in days (30 days). Backing store uses hours. */
export const DEFAULT_GUEST_AUTH_TTL_DAYS = 30;

/** Minimum guest auth session TTL in days (inclusive). */
export const MIN_GUEST_AUTH_TTL_DAYS = 1;

/** Maximum guest auth session TTL in days (inclusive, one year). */
export const MAX_GUEST_AUTH_TTL_DAYS = 365;

/** Maximum preset application types stored on organization chemicals config. */
export const MAX_ORGANIZATION_DEFAULT_APPLICATION_TYPE_PRESETS = 5;

/**
 * Chemicals config document at /organizations/{orgId}/config/chemicals.
 * Controls behavior for the chemicals area (e.g. usage log retention).
 */
export interface OrganizationConfigChemicals {
	/**
	 * Number of days that chemical usage logs are considered "alive".
	 * Only usage logs with createdAt on or after (now - usageLogAliveDays) are
	 * returned in the portal and companion app. Defaults to 365 when doc is absent.
	 */
	usageLogAliveDays: number;

	/**
	 * When true, a monthly chemical usage report (last 30 days) is sent to the
	 * organization owner(s) on the 1st of every month. Not implemented yet.
	 */
	monthlyReportEnabled?: boolean;

	/**
	 * Up to {@link MAX_ORGANIZATION_DEFAULT_APPLICATION_TYPE_PRESETS} presets applied
	 * when creating a new chemical in the catalog (names must be non-empty; REI optional).
	 */
	defaultApplicationTypePresets?: ApplicationTypeConfig[];
}

/**
 * Authentication config document at /organizations/{orgId}/config/authentication.
 * Controls authentication behavior for companion apps (e.g. guest session TTL).
 */
export interface OrganizationConfigAuthentication {
	/**
	 * Guest authentication session TTL in hours.
	 * Controls how long guest sessions remain valid before requiring re-authentication.
	 * Defaults to 720 hours (30 days) when doc is absent.
	 * Only affects newly authenticated guest accounts.
	 */
	guestAuthTtlHours: number;
}

/**
 * Users config document at /organizations/{orgId}/config/users.
 * Controls user-invite behavior for the organization.
 */
export interface OrganizationConfigUsers {
	/**
	 * Number of days a new or resent email invite remains valid after it is sent.
	 * Defaults to {@link DEFAULT_INVITE_EXPIRATION_DAYS} when doc is absent.
	 * Clamped to {@link MIN_INVITE_EXPIRATION_DAYS}–{@link MAX_INVITE_EXPIRATION_DAYS} when read by the API.
	 */
	inviteExpirationDays: number;
}
